import React, { type ReactElement, memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { TOOLTIP_OFFSET } from 'reactApp/components/SharingWindow/Sharing.constants';
import type { EAccessRights, IAccessRightsOptions } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingDropdown } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown';
import { EDropdownColors } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown.types';
import { ACCESS_RIGHTS_OPTIONS_FOLDER_BIZ, getAccessRightsOptions } from 'reactApp/components/SharingWindow/ui/User/User.constants';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';

import styles from './SharingNewAddUser.css';

interface Props {
    isInputDisabled: boolean;
    redesign: boolean;
    accessRights: EAccessRights;
    setAccessRights: (rights: EAccessRights) => void;
    variant?: SHARING_VARIANT;
    isFolder: boolean;
}

export const SharingAddUserAccessRights = memo(
    ({ isInputDisabled, redesign = false, accessRights, setAccessRights, variant, isFolder }: Props) => {
        const isPhone = useSelector(EnvironmentSelectors.isPhone);

        const rightsList = useMemo(() => {
            if (variant === SHARING_VARIANT.foldersBiz) {
                return ACCESS_RIGHTS_OPTIONS_FOLDER_BIZ;
            }
            return getAccessRightsOptions(isFolder);
        }, [variant]);

        const renderAccessRightsDropdown = useCallback(
            (onClick): ReactElement => {
                const list = Object.values(rightsList).map((item: IAccessRightsOptions) => ({
                    id: item.key,
                    text: item.dropdownText,
                    check: item.key === accessRights,
                    qaValue: item.key,
                }));

                return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
            },
            [accessRights, rightsList]
        );

        const item = rightsList[accessRights];

        return (
            <div className={styles.rightsItem}>
                <Dropdown
                    withinDialog
                    theme="octavius"
                    qaValue={item.key}
                    as={SharingDropdown}
                    value={redesign ? item.text.toLowerCase() : item.text}
                    color={redesign ? EDropdownColors.BLUE : EDropdownColors.GRAY}
                    placement={tooltipPlacements.BOTTOM_LEFT}
                    content={renderAccessRightsDropdown}
                    onClickItem={setAccessRights}
                    inInput
                    disabled={isInputDisabled}
                    tooltipOffset={TOOLTIP_OFFSET}
                    animatedTooltip
                    redesign={redesign}
                    isPhone={isPhone}
                    noPageOffset
                />
            </div>
        );
    }
);

SharingAddUserAccessRights.displayName = 'SharingAddUserAccessRights';

export default SharingAddUserAccessRights;
